import { useState, type FormEvent } from "react";
import { Navigate, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { api, formatApiError } from "../lib/api";

export function EvidenceNew() {
  const { slug } = useParams<{ slug: string }>();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const requirementUuid = params.get("requirementUuid");

  const [documentTitle, setDocumentTitle] = useState("");
  const [description, setDescription] = useState("");
  const [reviewPeriodMonths, setReviewPeriodMonths] = useState<number | "">("");
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);
  const [createdUuid, setCreatedUuid] = useState<string | null>(null);

  const onSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setSubmitting(true);
    setErrors([]);
    try {
      const result = await api<{ uuid: string }>("/evidence", {
        method: "POST",
        body: {
          documentTitle,
          description: description.trim() || null,
          reviewPeriodMonths: reviewPeriodMonths === "" ? null : Number(reviewPeriodMonths),
          linkedRequirementUuids: requirementUuid ? [requirementUuid] : [],
        },
      });
      setCreatedUuid(result.uuid);
    } catch (err) {
      setErrors(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  if (createdUuid) {
    return <Navigate to={`/o/${slug}/vault/${createdUuid}`} replace />;
  }

  return (
    <div className="max-w-2xl space-y-6">
      <div>
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="text-xs font-medium text-brand-600 hover:text-brand-700 dark:text-brand-300"
        >
          ← Back
        </button>
        <h1 className="mt-1 text-2xl font-semibold text-slate-900 dark:text-slate-100">
          New evidence
        </h1>
        <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
          Create the record first, then upload one or more files and transition it through the
          approval workflow.
        </p>
      </div>

      <form onSubmit={onSubmit} className="space-y-4" noValidate>
        <div>
          <label
            htmlFor="ev-title"
            className="block text-sm font-medium text-slate-700 dark:text-slate-200"
          >
            Document title
          </label>
          <input
            id="ev-title"
            type="text"
            required
            value={documentTitle}
            onChange={(e) => setDocumentTitle(e.target.value)}
            aria-invalid={errors.length > 0 || undefined}
            aria-describedby={errors.length > 0 ? "ev-errors" : undefined}
            className="mt-1 block w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm shadow-sm focus:border-brand-500 focus:ring-brand-500 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100"
          />
        </div>

        <div>
          <label
            htmlFor="ev-desc"
            className="block text-sm font-medium text-slate-700 dark:text-slate-200"
          >
            Description (optional)
          </label>
          <textarea
            id="ev-desc"
            rows={3}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            aria-invalid={errors.length > 0 || undefined}
            aria-describedby={errors.length > 0 ? "ev-errors" : undefined}
            className="mt-1 block w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm shadow-sm focus:border-brand-500 focus:ring-brand-500 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100"
          />
        </div>

        <div>
          <label
            htmlFor="ev-review"
            className="block text-sm font-medium text-slate-700 dark:text-slate-200"
          >
            Review period (months)
          </label>
          <input
            id="ev-review"
            type="number"
            min={1}
            max={120}
            value={reviewPeriodMonths}
            onChange={(e) =>
              setReviewPeriodMonths(e.target.value === "" ? "" : Number(e.target.value))
            }
            aria-invalid={errors.length > 0 || undefined}
            aria-describedby={errors.length > 0 ? "ev-errors" : undefined}
            className="mt-1 block w-32 rounded-md border border-slate-300 bg-white px-3 py-2 text-sm shadow-sm focus:border-brand-500 focus:ring-brand-500 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100"
          />
          <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
            Optional. The system will flag the document for review this many months after it is
            authorised.
          </p>
        </div>

        {requirementUuid ? (
          <div className="rounded border border-brand-200 bg-brand-50 p-3 text-xs text-brand-900 dark:border-brand-900/40 dark:bg-brand-500/10 dark:text-brand-100">
            This evidence will be linked to the clause you came from.
          </div>
        ) : null}

        {errors.length > 0 ? (
          <div
            id="ev-errors"
            role="alert"
            className="rounded border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-950/40 dark:text-red-200"
          >
            {errors.length === 1 ? (
              errors[0]
            ) : (
              <ul className="list-disc space-y-0.5 pl-4">
                {errors.map((m, i) => (
                  <li key={i}>{m}</li>
                ))}
              </ul>
            )}
          </div>
        ) : null}

        <div className="flex gap-2">
          <button
            type="submit"
            disabled={submitting}
            className="rounded-md bg-brand-600 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-700 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {submitting ? "Creating…" : "Create evidence"}
          </button>
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="rounded-md border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700"
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
}
